import { AfterViewInit, Component, Input, OnInit, Renderer2, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { AssetModel } from '../../models/asset.model';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { AppService } from '../../app.service';
import { ApiRequestService } from '../../shared/api-request.service';
import { UtilsService } from '../../shared/utils.service';
import { tap } from 'rxjs/operators';
import { ApiDataSource } from '../../utils/api-data-source';
import { MatDialog } from '@angular/material/dialog';
import { AssetMaintenanceScheduleModel } from '../../models/asset-maintenance-schedule.model';
import {
  AssetMaintenanceSchedulesEditComponent
} from './asset-maintenance-schedules-edit/asset-maintenance-schedules-edit.component';
import {
  AssetMaintenanceSchedulesViewComponent
} from './asset-maintenance-schedules-view/asset-maintenance-schedules-view.component';
import {
  AssetMaintenanceSchedulesFilterComponent
} from './asset-maintenance-schedules-filter/asset-maintenance-schedules-filter.component';
import { AssetMaintenanceSchedulesService } from './asset-maintenance-schedules.service';
import { AssetMaintenanceRecordsService } from '../asset-maintenance-records/asset-maintenance-records.service';
import {
  AssetMaintenanceSchedulesFilters
} from './asset-maintenance-schedules-filter/asset-maintenance-schedules-filter.interface';
import { UserPublicProfileComponent } from '../../shared/user-public-profile/user-public-profile.component';
import { RBACService } from '../../shared/rbac.service';
import { assetUserCanAccess } from '../assets-permissions';
import { InfoDialogComponent } from '../../shared/info-dialog/info-dialog.component';
import { ASSET_MAINTENANCE_SCHEDULES_FLAGS_LEGEND } from './asset-maintenance-schedules-flags-legend';

@Component({
  selector: 'app-asset-maintenance-schedules',
  templateUrl: './asset-maintenance-schedules.component.html',
  styleUrls: ['./asset-maintenance-schedules.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class AssetMaintenanceSchedulesComponent implements OnInit, AfterViewInit {

  @Input('asset_id') asset_id: number;
  @Input('asset') asset: AssetModel;

  dataSource: AssetMaintenanceSchedulesDataSource = new AssetMaintenanceSchedulesDataSource(this.app, this.api);

  // Built dynamically in ngOnInit(). The checkbox column only drives Archive Selected and Restore
  // Selected, so it is added only for a user who can delete (see canDelete below).
  listColumns: string[] = [];

  // the paginator and sorter
  @ViewChild('paginator1') paginator1: MatPaginator;
  @ViewChild('paginator2') paginator2: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  readonly MAX_VISIBLE_ASSIGNED_USERS = 3;

  // RBAC permission flags: resolved once in ngOnInit(). Maintenance schedules belong to an asset, so
  // they are governed by the asset permissions, evaluated across both the account level and the site
  // level asset perspectives because this tab is reached from both.
  canCreate: boolean = false;
  canView: boolean = false;
  canEdit: boolean = false;
  canDelete: boolean = false;

  constructor(
    public utils: UtilsService,
    public app: AppService,
    private api: ApiRequestService,
    private dialog: MatDialog,
    public renderer: Renderer2,
    public assetMaintenanceRecordsService: AssetMaintenanceRecordsService,
    public assetMaintenanceSchedulesService: AssetMaintenanceSchedulesService,
    public rbac: RBACService
  ) { }

  ngOnInit() {
    this.canCreate = assetUserCanAccess(this.rbac, RBACService.RBAC_A.A_CREATE);
    this.canView = assetUserCanAccess(this.rbac, RBACService.RBAC_A.A_VIEW);
    this.canEdit = assetUserCanAccess(this.rbac, RBACService.RBAC_A.A_EDIT);
    this.canDelete = assetUserCanAccess(this.rbac, RBACService.RBAC_A.A_DELETE);

    // Build the columns dynamically. Selection only drives archive and restore here (there is no
    // export), so the checkbox column is only useful to a user who can delete.
    this.listColumns = [];
    if (this.canDelete) {
      this.listColumns.push('select');
    }
    this.listColumns.push(
      'id',
      'title',
      // 'schedule_mode', // Phase 4
      'maintenance_type',
      'flags',
      'default_priority',
      'next_due_at',
      'next_auto_create_at',
      'users',
      'date_created',
      'actions'
    );

    // Only load the list when the user may view it.
    if (this.canView) {
      this.dataSource.asset_id = this.asset_id;
      this.dataSource.getData(true);
    }
  }

  ngAfterViewInit() {
    // The table, its matSort and its mat-paginator all live inside a container gated by
    // *ngIf="canView", so without that permission Angular never creates those view
    // children and this.sort / this.paginator stay undefined. Bail out before wiring them up.
    if (!this.canView) {
      return;
    }

    // Reset the paginator when sorting takes place
    this.sort.sortChange.subscribe(() => {
      this.paginator1.pageIndex = 0;
      this.paginator2.pageIndex = 0;
    });

    const paginatorTap = tap((paginator) => {
      this.paginator1.pageIndex = paginator['pageIndex'];
      this.paginator1.pageSize = paginator['pageSize'];
      this.paginator2.pageIndex = paginator['pageIndex'];
      this.paginator2.pageSize = paginator['pageSize'];

      this.dataSource.limit = paginator['pageSize'];
      this.dataSource.offset = paginator['pageIndex'];
      this.dataSource.getData();
    });

    // Subscribe to the paginator tap events.
    this.paginator1.page.pipe(paginatorTap).subscribe();
    this.paginator2.page.pipe(paginatorTap).subscribe();

    // Subscribe to the sorter tap events.
    this.sort.sortChange.pipe(tap((sorter) => {
      this.dataSource.order_by = sorter['active'];
      this.dataSource.order = sorter['direction'];

      this.dataSource.getData();
    })).subscribe();
  }

  /**
   * Create a new Asset Maintenance Schedule.
   *
   * @return {void}
   */
  onAdd(): void {
    if (!this.canCreate) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to create maintenance schedules.');
      return;
    }

    this.openEditDialog(new AssetMaintenanceScheduleModel());
  }

  /**
   * Update the existing Asset Maintenance Schedule.
   *
   * @param {AssetMaintenanceScheduleModel} asset_maintenance_schedule The asset maintenance schedule to be edited.
   * @return {void}
   */
  onEdit(asset_maintenance_schedule: AssetMaintenanceScheduleModel): void {
    if (!this.canEdit) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to edit maintenance schedules.');
      return;
    }

    this.openEditDialog(asset_maintenance_schedule);
  }

  /**
   * Opens the create/edit dialog. Shared by onAdd() and onEdit(), each of which checks its own
   * permission first, so a create only user is never blocked by the edit check.
   *
   * @param {AssetMaintenanceScheduleModel} asset_maintenance_schedule The asset maintenance schedule to be edited.
   * @return {void}
   */
  private openEditDialog(asset_maintenance_schedule: AssetMaintenanceScheduleModel): void {
    this.dialog
      .open(AssetMaintenanceSchedulesEditComponent, {
        width: '90%',
        data: {
          asset_id: this.asset_id,
          asset_maintenance_schedule_id: asset_maintenance_schedule.id
        }
      })
      .afterClosed()
      .subscribe(() => {
        // Refresh the list regardless of how the dialog is closed, as long as the user may view it.
        if (this.canView) {
          this.dataSource.getData(true);
        }
      });
  }

  /**
   * Display the Asset Maintenance Schedule data.
   *
   * @param {number} id - The unique identifier of the asset maintenance schedule.
   * @return {void}
   */
  onView(id: number): void {
    if (!this.canView) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to view maintenance schedules.');
      return;
    }

    this.utils.showComponentDialog(
      AssetMaintenanceSchedulesViewComponent,
      {
        asset_id: this.asset_id,
        asset_maintenance_schedule_id: id
      },
      { width: '700px' },
      () => {
        // Refresh the list regardless of how the dialog is closed.
        if (this.canView) {
          this.dataSource.getData(true);
        }
      }
    );
  }

  /**
   * Archives selected Asset Maintenance Schedules.
   *
   * @return {void}
   */
  onRemoveSelected(): void {
    if (!this.canDelete) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to archive maintenance schedules.');
      return;
    }

    this.utils.showModal(
      'Archive Asset Maintenance Schedule(s)',
      `Are you sure you want to archive ${this.dataSource.selection.selected.length} asset maintenance schedule(s)?`,
      () => {
        this.remove(this.dataSource.selection.selected.join(','), 'Asset Maintenance Schedule(s) have been archived.');
      }
    );
  }

  /**
   * Archives the specified Asset Maintenance Schedule.
   *
   * @param {AssetMaintenanceScheduleModel} asset_maintenance_schedule - The asset maintenance schedule to be archived.
   * @return {void}
   */
  onRemove(asset_maintenance_schedule: AssetMaintenanceScheduleModel): void {
    if (!this.canDelete) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to archive maintenance schedules.');
      return;
    }

    this.utils.showModal(
      'Archive Asset Maintenance Schedule',
      `Are you sure you want to archive "${asset_maintenance_schedule.title}"?`,
      () => {
        this.remove(asset_maintenance_schedule.id, 'Asset Maintenance Schedule has been archived.');
      }
    );
  }

  /**
   * Archives Asset Maintenance Schedules with the specified IDs.
   *
   * @param {any} ids - IDs of asset maintenance schedules to be removed.
   * @param msg
   * @return {void}
   */
  private remove(ids: any, msg: string): void {
    this.api.makeRequest('delete', `v2/assets/${this.asset_id}/maintenance-schedules/${ids}`)
      .then((response) => {
        this.utils.showToast(msg);
        this.dataSource.selection.clear();
        this.dataSource.getData(false);
      })
      .catch((errorResponse) => {
        this.utils.handleAPIErrors(errorResponse);
      });
  }

  /**
   * Restore Asset Maintenance Schedule.
   *
   * @param {number} id - The ID of the asset maintenance schedule to be restored.
   * @return {void}
   */
  onRestore(id: number): void {
    if (!this.canDelete) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to restore maintenance schedules.');
      return;
    }

    this.utils.showModal(
      'Restore Asset Maintenance Schedule',
      'Are you sure you want to restore this asset maintenance schedule?',
      () => {
        this.restore(id, 'The Asset Maintenance Schedule was restored.');
      }
    );
  }

  /**
   * Restore selected Asset Maintenance Schedules.
   *
   * @return {void}
   */
  onRestoreSelected(): void {
    if (!this.canDelete) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to restore maintenance schedules.');
      return;
    }

    this.utils.showModal(
      'Restore Selected Asset Maintenance Schedule(s)',
      'Are you sure you want to restore the selected asset maintenance schedules?',
      () => {
        this.restore(this.dataSource.selection.selected.join(','), 'The selected Asset Maintenance Schedule(s) were restored.');
      }
    );
  }

  /**
   * Restores Asset Maintenance Schedules with the specified IDs.
   *
   * @param {any} ids - IDs of asset maintenance schedules to be restored.
   * @param msg
   * @return {void}
   */
  private restore(ids: any, msg: string): void {
    this.api.makeRequest('put', `v2/assets/${this.asset_id}/maintenance-schedules/${ids}/restore`)
      .then((response) => {
        this.utils.showToast(msg);
        this.dataSource.selection.clear();
        this.dataSource.getData();
      })
      .catch((errorResponse) => {
        this.utils.handleAPIErrors(errorResponse);
      });
  }

  /**
   * Open the filter dialog and apply the selected filters.
   *
   * @return {void}
   */
  onFilter(): void {
    if (!this.canView) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to view maintenance schedules.');
      return;
    }

    this.utils.showComponentDialog(AssetMaintenanceSchedulesFilterComponent, {
        archived: this.dataSource.filters.archived,
        contractor_ids: this.dataSource.filters.contractor_ids,
        vendor_ids: this.dataSource.filters.vendor_ids,
        user_ids: this.dataSource.filters.user_ids,
        maintenance_type: this.dataSource.filters.maintenance_type,
        default_priority: this.dataSource.filters.default_priority,
        enabled: this.dataSource.filters.enabled,
        is_recurring: this.dataSource.filters.is_recurring,
        auto_create: this.dataSource.filters.auto_create,
        date_type: this.dataSource.filters.date_type,
        date_range: this.dataSource.filters.date_range
      }, {
        width: '1200px'
      },
      (results: any): void => {
        // Terminate early if it is undefined or an empty string.
        if ( typeof results === 'undefined' || results === '' ) {
          return;
        }

        // Apply the filters.
        this.dataSource.filters.archived = results.archived ?? this.dataSource.filters.archived;
        this.dataSource.filters.contractor_ids = results.contractor_ids ?? this.dataSource.filters.contractor_ids;
        this.dataSource.filters.vendor_ids = results.vendor_ids ?? this.dataSource.filters.vendor_ids;
        this.dataSource.filters.user_ids = results.user_ids ?? this.dataSource.filters.user_ids;
        this.dataSource.filters.maintenance_type = results.maintenance_type ?? this.dataSource.filters.maintenance_type;
        this.dataSource.filters.default_priority = results.default_priority ?? this.dataSource.filters.default_priority;
        this.dataSource.filters.enabled = results.enabled ?? this.dataSource.filters.enabled;
        this.dataSource.filters.is_recurring = results.is_recurring ?? this.dataSource.filters.is_recurring;
        this.dataSource.filters.auto_create = results.auto_create ?? this.dataSource.filters.auto_create;
        this.dataSource.filters.date_type = results.date_type ?? this.dataSource.filters.date_type;
        this.dataSource.filters.date_range = results.date_range ?? this.dataSource.filters.date_range;
        this.dataSource.getData();
      }
    );
  }

  /**
   * Retrieves a tooltip string containing names of remaining users beyond a maximum visible limit.
   *
   * @param {any[]} users - An array of user objects.
   * @returns {string} A string containing the names of users beyond the maximum visible count.
   */
  getRemainingUsersTooltip(users: any[]): string {
    if (!users || users.length <= this.MAX_VISIBLE_ASSIGNED_USERS) {
      return '';
    }

    return users
      .slice(this.MAX_VISIBLE_ASSIGNED_USERS)
      .map(user => user.contact_person)
      .join(', ');
  }

  /**
   * Open the User's public profile dialog.
   *
   * @param hash
   * @return {void}
   */
  onUserPublicView(hash: string): void {
    this.utils.showComponentDialog(
      UserPublicProfileComponent,
      hash,
      { width: '90%' },
      () => {
        // Refresh the list regardless of how the dialog is closed.
      }
    );
  }

  /**
   * Open the information dialog explaining the Maintenance Schedule flags.
   *
   * @return {void}
   */
  onOpenFlagsLegend(): void {
    this.utils.showComponentDialog(
      InfoDialogComponent,
      ASSET_MAINTENANCE_SCHEDULES_FLAGS_LEGEND,
      {
        width: '760px'
      }
    );
  }

}

export class AssetMaintenanceSchedulesDataSource extends ApiDataSource {
  order_by = 'id';
  order = 'asc';
  asset_id: number;

  filters: AssetMaintenanceSchedulesFilters = {
    archived: 'false',
    contractor_ids: [],
    vendor_ids: [],
    user_ids: [],
    maintenance_type: 'all',
    default_priority: 'all',
    enabled: 'all',
    is_recurring: 'all',
    auto_create: 'all',
    date_type: 'next_due_at',
    date_range: []
  };

  getData(resetOffset: boolean = false) {
    this.makeRequest(`v2/assets/${this.asset_id}/maintenance-schedules`, resetOffset, {
      archived: this.filters.archived,
      contractor_ids: this.filters.contractor_ids.join(','),
      vendor_ids: this.filters.vendor_ids.join(','),
      user_ids: this.filters.user_ids.join(',') ,
      maintenance_type: this.filters.maintenance_type ?? 'all',
      default_priority: this.filters.default_priority ?? 'all',
      enabled: this.filters.enabled ?? 'all',
      is_recurring: this.filters.is_recurring ?? 'all',
      auto_create: this.filters.auto_create ?? 'all',
      date_type: this.filters.date_type,
      date_range: this.filters.date_range.map((date: Date) => {
        return date.getTime() / 1000;
      }).join(',')
    });
  }
}
